import { Button, Distribute, Spacer, Text } from '@loomhq/lens';
import React, { Suspense, lazy } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  startVideoWithoutPlaying,
  updateTheaterMode,
} from 'actions/videoPlayer';

import { selectCurrentVideoId, selectVideoViews } from 'selectors/videoPlayer';

import {
  initForVideo,
  trackActivate,
  trackExit,
  trackShown,
} from './analytics';
import { getColorAt } from './avatars';
import { ViewerAvatar } from './components';

import styles from './box.module.less';

const ViewerInsightsList = lazy(() => import('./list'));
const MAX_AVATARS = 12;

export default function ViewerInsights(props) {
  const views = useSelector(selectVideoViews);
  const videoId = useSelector(selectCurrentVideoId);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!videoId) {
      return;
    }

    initForVideo(videoId);
    trackShown();
  }, [videoId]);

  const onActivate = e => {
    dispatch(updateTheaterMode(false));
    dispatch(startVideoWithoutPlaying());
    props.onActivate(e);
    trackActivate();
  };
  const onExit = e => {
    props.onActivate(e);
    trackExit();
  };

  return (
    <div className="viewer-insights">
      {props.activated ? (
        <Suspense fallback={<div>Loading...</div>}>
          <ViewerInsightsList onExit={onExit} />
        </Suspense>
      ) : (
        <ViewerInsightsBox onActivate={onActivate} views={views} />
      )}
    </div>
  );
}

const ViewerInsightsBox = ({ views, onActivate }) => {
  const totalViews = views?.total || 0;
  const distinctViews = views?.distinct || 0;
  const namedViews = views?.named || [];
  const viewers = formatViewers(namedViews, distinctViews);
  const remaining = distinctViews - viewers.length;

  return (
    <div>
      <Header views={totalViews} />
      {totalViews ? (
        <>
          <Avatars viewers={viewers} remaining={remaining} />
          <Button
            variant="neutral"
            onClick={onActivate}
            hasFullWidth
            icon={<EIcon />}
          >
            See Engagement
          </Button>
        </>
      ) : (
        <Spacer top="medium">
          <Text size="small" color="bodyDimmed" alignment="center">
            Get a view to see engagement
          </Text>
        </Spacer>
      )}
    </div>
  );
};

const Header = ({ views }) => (
  <Distribute isSpread alignment="center">
    <Text fontWeight="semiBold">Viewers</Text>
    <Text>{views} Total Views</Text>
  </Distribute>
);

const Avatars = ({ viewers = [], remaining = 0 }) => (
  <div className={styles.engagementBox}>
    {viewers.map((viewer = {}, i) => (
      <ViewerAvatar
        key={`avatarList-${i}`}
        avatar={viewer.avatar}
        variant={(i + remaining) % 4}
        color={getColorAt(i + remaining)}
      />
    ))}
    {remaining > 0 && (
      <Text htmlTag="p">
        <strong>+{remaining}</strong> &nbsp;
      </Text>
    )}
  </div>
);

const EIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 5c.77 0 1.4.63 1.4 1.4v11.2c0 .77-.63 1.4-1.4 1.4-.77 0-1.4-.63-1.4-1.4V6.4c0-.77.63-1.4 1.4-1.4zM6.4 9.2h.2c.77 0 1.4.63 1.4 1.4v7c0 .77-.63 1.4-1.4 1.4h-.2c-.77 0-1.4-.63-1.4-1.4v-7c0-.77.63-1.4 1.4-1.4zM19 14.4c0-.77-.63-1.4-1.4-1.4-.77 0-1.4.63-1.4 1.4v3.2c0 .77.63 1.4 1.4 1.4.77 0 1.4-.63 1.4-1.4v-3.2z"
      fill="currentColor"
    ></path>
  </svg>
);

function formatViewers(views = [], total = 0) {
  const maxAvatars = Math.min(MAX_AVATARS, total);
  const named = views.slice(0, maxAvatars);

  return [...named, ...Array(maxAvatars)].slice(0, maxAvatars);
}
